// CR051/CR052: Runs & Outputs Tab - Capture run history with step telemetry + health dashboard
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { Loader2, RefreshCw, Play, Trash2, Eye, FileText, Image, CheckCircle, XCircle, AlertTriangle, Clock, Activity, Wrench, ChevronDown, Download, ExternalLink, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface RunsOutputsTabProps {
  merchantId: string;
  merchantName: string;
}

interface HealthSignals {
  screenshots_count: number;
  unique_pages_count: number;
  pdf_page_count: number;
  repeat_detected: boolean;
  repeat_at_page: number | null;
  first_last_page_similarity: number;
  header_chrome_ratio: number;
  viewer_change_rate: number;
}

interface CaptureHealth {
  status: 'PASS' | 'WARN' | 'FAIL';
  score: number;
  signals: HealthSignals;
  reasons: string[];
  recommended_tweaks: string[];
}

interface ArtifactRef {
  path: string;
  signed_url: string | null;
}

interface OutputsContract {
  screenshot_count: number;
  pdf_page_count: number;
  artifacts: {
    screenshots: ArtifactRef[];
    stitched_pdf: ArtifactRef | null;
  };
  debug?: {
    failed_requests: string[];
  };
}

interface CaptureRun {
  id: string;
  merchant_id: string;
  merchant_name: string;
  recipe_id: string | null;
  recipe_snapshot_hash: string;
  effective_config_snapshot: Record<string, unknown>;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  outputs: OutputsContract | Record<string, unknown>;
  errors: unknown[];
  capture_health: CaptureHealth | null;
  health_status: string | null;
  health_score: number | null;
  health_signals: HealthSignals | null;
  health_recommended_tweaks: string[] | null;
  stop_reason: string | null;
  fallback_used: boolean;
  created_at: string;
}

interface CaptureArtifact {
  id: string;
  run_id: string;
  artifact_type: string;
  page_index: number | null;
  storage_path: string;
  content_type: string | null;
  size_bytes: number | null;
  metadata: Record<string, unknown>;
  created_at: string;
  signed_url?: string | null;
}

interface StepTelemetry {
  id: string;
  run_id: string;
  step_index: number;
  step_type: string;
  step_name: string | null;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  inputs_snapshot: Record<string, unknown>;
  outputs_snapshot: Record<string, unknown>;
  error_message: string | null;
  artifact_ids: string[];
  created_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  running: { label: 'Running', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2 },
  stopping: { label: 'Stopping', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', icon: StopCircle },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', icon: AlertTriangle },
};

const HEALTH_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  PASS: { label: 'PASS', color: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  WARN: { label: 'WARN', color: 'text-yellow-700 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
  FAIL: { label: 'FAIL', color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/30' },
};

export const RunsOutputsTab = ({ merchantId, merchantName }: RunsOutputsTabProps) => {
  const [runs, setRuns] = useState<CaptureRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState<CaptureRun | null>(null);
  const [artifacts, setArtifacts] = useState<CaptureArtifact[]>([]);
  const [steps, setSteps] = useState<StepTelemetry[]>([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Bulk delete state
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [stoppingRunId, setStoppingRunId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadRuns();
    setSelectedRunIds(new Set());
  }, [merchantId]);

  const loadRuns = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('capture_console_runs')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setRuns((data || []) as unknown as CaptureRun[]);
    } catch (err) {
      console.error('[CR052] Load runs error:', err);
      toast.error('Failed to load runs');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRunDetails = async (runId: string) => {
    setIsLoadingDetails(true);
    try {
      const [artifactsRes, stepsRes] = await Promise.all([
        supabase
          .from('capture_console_artifacts')
          .select('*')
          .eq('run_id', runId)
          .order('page_index', { ascending: true }),
        supabase
          .from('capture_console_run_steps')
          .select('*')
          .eq('run_id', runId)
          .order('step_index', { ascending: true })
      ]);

      if (artifactsRes.error) throw artifactsRes.error;
      if (stepsRes.error) throw stepsRes.error;
      
      // Fetch signed URLs for all artifacts
      const artifactsData = (artifactsRes.data || []) as unknown as CaptureArtifact[];
      const artifactsWithUrls = await Promise.all(
        artifactsData.map(async (artifact) => {
          const { data } = await supabase.storage
            .from('flyer-poc-assets')
            .createSignedUrl(artifact.storage_path, 3600); // 1 hour expiry
          return { ...artifact, signed_url: data?.signedUrl || null };
        })
      );
      
      setArtifacts(artifactsWithUrls);
      setSteps((stepsRes.data || []) as unknown as StepTelemetry[]);
    } catch (err) {
      console.error('[CR052] Load run details error:', err);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleViewRun = (run: CaptureRun) => {
    setSelectedRun(run);
    loadRunDetails(run.id);
  };

  const handleDeleteSelected = async () => {
    if (selectedRunIds.size === 0) return;
    setIsDeleting(true);
    try {
      // Delete artifacts from storage first
      for (const runId of selectedRunIds) {
        const { data: artifactData } = await supabase
          .from('capture_console_artifacts')
          .select('storage_path')
          .eq('run_id', runId);
        
        if (artifactData) {
          for (const artifact of artifactData) {
            await supabase.storage.from('flyer-poc-assets').remove([artifact.storage_path]);
          }
        }
      }

      const { error } = await supabase
        .from('capture_console_runs')
        .delete()
        .in('id', Array.from(selectedRunIds));

      if (error) throw error;
      
      setRuns(prev => prev.filter(r => !selectedRunIds.has(r.id)));
      toast.success(`Deleted ${selectedRunIds.size} run(s) and their artifacts`);
      setSelectedRunIds(new Set());
    } catch (err) {
      console.error('[CR052] Delete runs error:', err);
      toast.error('Failed to delete runs');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const toggleRunSelection = (runId: string) => {
    setSelectedRunIds(prev => {
      const next = new Set(prev);
      next.has(runId) ? next.delete(runId) : next.add(runId);
      return next;
    });
  };

  const toggleAllRuns = () => {
    setSelectedRunIds(selectedRunIds.size === runs.length ? new Set() : new Set(runs.map(r => r.id)));
  };

  const handleStopRun = async (runId: string) => {
    setStoppingRunId(runId);
    try {
      // Update status to stopping first
      const { error } = await supabase
        .from('capture_console_runs')
        .update({ status: 'stopping' })
        .eq('id', runId)
        .eq('status', 'running');

      if (error) throw error;
      
      // Update local state
      setRuns(prev => prev.map(r => 
        r.id === runId ? { ...r, status: 'stopping' } : r
      ));
      
      toast.success('Stop requested - run will halt after current step');
    } catch (err) {
      console.error('[CR052] Stop run error:', err);
      toast.error('Failed to stop run');
    } finally {
      setStoppingRunId(null);
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatBytes = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const getOutputs = (run: CaptureRun): OutputsContract | null => {
    const outputs = run.outputs;
    if (outputs && 'screenshot_count' in outputs) {
      return outputs as OutputsContract;
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Capture Runs</h2>
          <p className="text-sm text-muted-foreground">{runs.length} runs for {merchantName}</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedRunIds.size > 0 && (
            <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedRunIds.size})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={loadRuns} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Runs Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : runs.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              No capture runs yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedRunIds.size === runs.length && runs.length > 0}
                      onCheckedChange={toggleAllRuns}
                    />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Outputs</TableHead>
                  <TableHead>Stop Reason</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => {
                  const statusConfig = STATUS_CONFIG[run.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;
                  const healthStatus = run.health_status || (run.capture_health?.status);
                  const healthScore = run.health_score ?? run.capture_health?.score;
                  const healthConfig = healthStatus ? HEALTH_CONFIG[healthStatus] : null;
                  const outputs = getOutputs(run);

                  return (
                    <TableRow key={run.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedRunIds.has(run.id)}
                          onCheckedChange={() => toggleRunSelection(run.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("flex items-center gap-1 w-fit", statusConfig.color)}>
                          <StatusIcon className={cn("h-3 w-3", run.status === 'running' && "animate-spin")} />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {healthConfig ? (
                          <Badge className={cn("font-mono", healthConfig.bgColor, healthConfig.color)}>
                            {healthConfig.label}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {healthScore != null ? (
                          <div className="flex items-center gap-2">
                            <Progress value={healthScore} className="w-16 h-2" />
                            <span className="text-xs font-mono">{healthScore}</span>
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{formatDuration(run.duration_ms)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          {outputs ? (
                            <>
                              <span className="flex items-center gap-1">
                                <Image className="h-3 w-3" />
                                {outputs.screenshot_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {outputs.pdf_page_count}
                              </span>
                            </>
                          ) : '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-xs">{run.stop_reason || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {run.started_at ? format(new Date(run.started_at), 'MMM d, HH:mm') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(run.status === 'running' || run.status === 'stopping') && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleStopRun(run.id)}
                              disabled={stoppingRunId === run.id || run.status === 'stopping'}
                              title="Stop run"
                            >
                              {stoppingRunId === run.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <StopCircle className="h-4 w-4 text-orange-600" />
                              )}
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => handleViewRun(run)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => {
                              setSelectedRunIds(new Set([run.id]));
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedRunIds.size} run(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected runs, their artifacts, and storage files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSelected}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Run Detail Dialog */}
      <Dialog open={!!selectedRun} onOpenChange={() => setSelectedRun(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              Run Details
              {selectedRun?.health_status && (
                <Badge className={cn(
                  "font-mono",
                  HEALTH_CONFIG[selectedRun.health_status]?.bgColor,
                  HEALTH_CONFIG[selectedRun.health_status]?.color
                )}>
                  {selectedRun.health_status} ({selectedRun.health_score || '-'})
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedRun?.id.slice(0, 8)}... • {selectedRun?.recipe_snapshot_hash && `Hash: ${selectedRun.recipe_snapshot_hash.slice(0, 12)}...`}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="health" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="health" className="flex items-center gap-1">
                <Activity className="h-3 w-3" />
                Health
              </TabsTrigger>
              <TabsTrigger value="steps" className="flex items-center gap-1">
                <Play className="h-3 w-3" />
                Steps ({steps.length})
              </TabsTrigger>
              <TabsTrigger value="artifacts" className="flex items-center gap-1">
                <Image className="h-3 w-3" />
                Artifacts ({artifacts.length})
              </TabsTrigger>
              <TabsTrigger value="config" className="flex items-center gap-1">
                <Wrench className="h-3 w-3" />
                Config
              </TabsTrigger>
              <TabsTrigger value="debug" className="flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Debug
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              {/* Health Tab */}
              <TabsContent value="health" className="m-0 space-y-4">
                {selectedRun?.capture_health || selectedRun?.health_signals ? (
                  <>
                    {/* Health Summary */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          Health Summary
                          {selectedRun.health_status && (
                            <Badge className={cn(
                              HEALTH_CONFIG[selectedRun.health_status]?.bgColor,
                              HEALTH_CONFIG[selectedRun.health_status]?.color
                            )}>
                              Score: {selectedRun.health_score || 0}/100
                            </Badge>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Progress value={selectedRun.health_score || 0} className="h-3 mb-4" />
                        
                        {/* Reasons */}
                        {(selectedRun.capture_health?.reasons || []).length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium mb-2">Issues Detected:</h4>
                            <ul className="space-y-1">
                              {selectedRun.capture_health?.reasons.map((reason, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Recommended Tweaks */}
                        {(selectedRun.health_recommended_tweaks || selectedRun.capture_health?.recommended_tweaks || []).length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium mb-2">Recommended Tweaks:</h4>
                            <ul className="space-y-1">
                              {(selectedRun.health_recommended_tweaks || selectedRun.capture_health?.recommended_tweaks || []).map((tweak, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <Wrench className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                  {tweak}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Health Signals */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Health Signals</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {(() => {
                            const signals = selectedRun.health_signals || selectedRun.capture_health?.signals;
                            if (!signals) return null;
                            return (
                              <>
                                <div className="p-3 bg-muted rounded-lg">
                                  <div className="text-xs text-muted-foreground">Screenshots</div>
                                  <div className="text-xl font-bold">{signals.screenshots_count}</div>
                                </div>
                                <div className="p-3 bg-muted rounded-lg">
                                  <div className="text-xs text-muted-foreground">Unique Pages</div>
                                  <div className="text-xl font-bold">{signals.unique_pages_count}</div>
                                </div>
                                <div className="p-3 bg-muted rounded-lg">
                                  <div className="text-xs text-muted-foreground">PDF Pages</div>
                                  <div className="text-xl font-bold">{signals.pdf_page_count}</div>
                                </div>
                                <div className="p-3 bg-muted rounded-lg">
                                  <div className="text-xs text-muted-foreground">Viewer Change Rate</div>
                                  <div className="text-xl font-bold">{(signals.viewer_change_rate * 100).toFixed(0)}%</div>
                                </div>
                                <div className="p-3 bg-muted rounded-lg">
                                  <div className="text-xs text-muted-foreground">Header Chrome Ratio</div>
                                  <div className={cn(
                                    "text-xl font-bold",
                                    signals.header_chrome_ratio > 0.5 && "text-destructive"
                                  )}>
                                    {(signals.header_chrome_ratio * 100).toFixed(0)}%
                                  </div>
                                </div>
                                <div className="p-3 bg-muted rounded-lg">
                                  <div className="text-xs text-muted-foreground">Repeat Detected</div>
                                  <div className="text-xl font-bold">
                                    {signals.repeat_detected ? `Yes @ ${signals.repeat_at_page}` : 'No'}
                                  </div>
                                </div>
                                <div className="p-3 bg-muted rounded-lg col-span-2">
                                  <div className="text-xs text-muted-foreground">First/Last Similarity</div>
                                  <div className="text-xl font-bold">
                                    {signals.first_last_page_similarity > 0 ? 'Identical' : 'Different'}
                                  </div>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No health data available for this run
                  </div>
                )}
              </TabsContent>

              {/* Steps Tab */}
              <TabsContent value="steps" className="m-0">
                {isLoadingDetails ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : steps.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">No step telemetry available</div>
                ) : (
                  <Accordion type="multiple" className="space-y-2">
                    {steps.map((step) => {
                      const isSuccess = step.status === 'success';
                      const isFailed = step.status === 'failed';
                      return (
                        <AccordionItem key={step.id} value={step.id} className="border rounded-lg">
                          <AccordionTrigger className="px-4 py-3 hover:no-underline">
                            <div className="flex items-center gap-4 w-full">
                              <span className="text-xs font-mono text-muted-foreground w-8">
                                #{step.step_index}
                              </span>
                              <Badge variant={isSuccess ? 'default' : isFailed ? 'destructive' : 'secondary'}>
                                {step.step_type}
                              </Badge>
                              <span className="text-sm flex-1 text-left truncate">
                                {step.step_name || step.step_type}
                              </span>
                              <span className="text-xs font-mono text-muted-foreground">
                                {formatDuration(step.duration_ms)}
                              </span>
                              {isSuccess && <CheckCircle className="h-4 w-4 text-green-500" />}
                              {isFailed && <XCircle className="h-4 w-4 text-red-500" />}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h5 className="text-xs font-medium mb-2">Inputs</h5>
                                <pre className="text-xs font-mono bg-muted p-3 rounded-lg overflow-auto max-h-32">
                                  {JSON.stringify(step.inputs_snapshot, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <h5 className="text-xs font-medium mb-2">Outputs</h5>
                                <pre className="text-xs font-mono bg-muted p-3 rounded-lg overflow-auto max-h-32">
                                  {JSON.stringify(step.outputs_snapshot, null, 2)}
                                </pre>
                              </div>
                            </div>
                            {step.error_message && (
                              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                <h5 className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">Error</h5>
                                <p className="text-sm text-red-600 dark:text-red-300">{step.error_message}</p>
                              </div>
                            )}
                            {step.artifact_ids.length > 0 && (
                              <div className="mt-4">
                                <h5 className="text-xs font-medium mb-2">Artifacts Generated: {step.artifact_ids.length}</h5>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}
              </TabsContent>

              {/* Artifacts Tab */}
              <TabsContent value="artifacts" className="m-0">
                {isLoadingDetails ? (
                  <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : artifacts.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">No artifacts</div>
                ) : (
                  <ScrollArea className="h-[400px] pr-4">
                    <div className="space-y-2">
                      {artifacts.map((artifact) => (
                      <Card key={artifact.id}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {artifact.artifact_type === 'screenshot' || artifact.artifact_type === 'flyer_page_image' ? (
                              <Image className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <FileText className="h-5 w-5 text-muted-foreground" />
                            )}
                            <div>
                              <div className="font-medium">
                                {artifact.artifact_type}
                                {artifact.page_index != null && ` (Page ${artifact.page_index + 1})`}
                              </div>
                              <div className="text-sm text-muted-foreground font-mono text-xs">
                                {artifact.storage_path} • {formatBytes(artifact.size_bytes)}
                              </div>
                            </div>
                          </div>
                          {artifact.signed_url ? (
                            <Button variant="outline" size="sm" asChild>
                              <a 
                                href={artifact.signed_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View
                              </a>
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" disabled>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              No URL
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                    </div>
                  </ScrollArea>
                )}
              </TabsContent>

              {/* Config Tab */}
              <TabsContent value="config" className="m-0">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Effective Config Snapshot</CardTitle>
                    <CardDescription>The merged configuration that was used for this run</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs font-mono bg-muted p-4 rounded-lg overflow-auto max-h-96">
                      {JSON.stringify(selectedRun?.effective_config_snapshot, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Debug Tab */}
              <TabsContent value="debug" className="m-0 space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Run Metadata</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-xs text-muted-foreground">Run ID</div>
                        <div className="font-mono text-xs">{selectedRun?.id}</div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-xs text-muted-foreground">Recipe Hash</div>
                        <div className="font-mono text-xs">{selectedRun?.recipe_snapshot_hash}</div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-xs text-muted-foreground">Stop Reason</div>
                        <div className="font-mono text-xs">{selectedRun?.stop_reason || 'N/A'}</div>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <div className="text-xs text-muted-foreground">Fallback Used</div>
                        <div className="font-mono text-xs">{selectedRun?.fallback_used ? 'Yes' : 'No'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Outputs Contract</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs font-mono bg-muted p-4 rounded-lg overflow-auto max-h-48">
                      {JSON.stringify(selectedRun?.outputs, null, 2)}
                    </pre>
                  </CardContent>
                </Card>

                {(selectedRun?.errors as unknown[])?.length > 0 && (
                  <Card className="border-destructive">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-destructive">Errors</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs font-mono bg-red-50 dark:bg-red-900/20 p-4 rounded-lg overflow-auto max-h-48 text-red-700 dark:text-red-300">
                        {JSON.stringify(selectedRun?.errors, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};
